import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';

import fdaApi from '../../../../api/data-sections/fdaApi';
import { useSystemError } from '../../../../components/Error/SystemErrorContext';
import { FdaPmaModel } from '../../../../models/data-sections/fda/pmaModel';
import { ResponseErrorModel } from '../../../../models/system/responseErrorModel';
import { ResponseSuccessModel } from '../../../../models/system/responseSuccessModel';
import { handleError, mapResponseErrorToSystemError, mapUnknownErrorToSystemError } from '../../../../utils/errorUtils';

interface PmaModalProps {
  searchExecutionId: string;
  selected_pma: FdaPmaModel;
}

const PmaModal: React.FC<PmaModalProps> = ({ searchExecutionId, selected_pma }) => {
  const [pmaData, setPmaData] = useState<FdaPmaModel | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [didErrorOccur, setDidErrorOccur] = useState(false);
  const { triggerSystemError } = useSystemError();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        await fetchPmaDetail(searchExecutionId, selected_pma.pma_number, selected_pma.supplement_number, new Date(selected_pma.__ci_system_search_date_key ?? 0));
      } catch (error) {
        console.error('Error during fetch:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [searchExecutionId, selected_pma]);

  const fetchPmaDetail = async (search_execution_id: string, pma_number: string, supplement_number: string, __ci_system_search_date_key: Date): Promise<void> => {
    try {
      const response = await fdaApi.getPmaDetail(search_execution_id, pma_number, supplement_number, __ci_system_search_date_key);

      if (response.success) {
        const data = (response as ResponseSuccessModel).data as FdaPmaModel[];
        setPmaData(data?.[0] || null);
      } else {
        handleError(mapResponseErrorToSystemError(response as ResponseErrorModel), null, setDidErrorOccur, triggerSystemError);
      }
    } catch (error) {
      handleError(mapUnknownErrorToSystemError(error, false), null, setDidErrorOccur, triggerSystemError);
    }
  };

  return (
    <>
      <Box display="flex" flexDirection="column" gap={1} mb={2}>
        <Typography variant="h6">
          {pmaData?.trade_name || 'PMA Application'}
        </Typography>
        <Typography variant="subtitle2" color="textSecondary">
          {selected_pma.pma_number} / {selected_pma.applicant}
        </Typography>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="200px">
          <CircularProgress />
        </Box>
      ) : didErrorOccur || !pmaData ? (
        <Typography color="error">Failed to load PMA details.</Typography>
      ) : (
        <Box mt={2}>
          <Typography><strong>Trade Name:</strong> {pmaData.trade_name || 'N/A'}</Typography>
          <Typography><strong>Generic Name:</strong> {pmaData.generic_name || 'N/A'}</Typography>
          <Typography><strong>Applicant:</strong> {pmaData.applicant || 'N/A'}</Typography>
          <Typography><strong>Supplement Number:</strong> {pmaData.supplement_number || 'N/A'}</Typography>
          <Typography><strong>Supplement Type:</strong> {pmaData.supplement_type || 'N/A'}</Typography>
          <Typography><strong>Decision Date:</strong> {pmaData.decision_date || 'N/A'}</Typography>
          <Typography><strong>Expedited Review:</strong> {pmaData.expedited_review_flag || 'N/A'}</Typography>
          <Typography><strong>Review Panel:</strong> {pmaData.advisory_committee || 'N/A'}</Typography>

          {pmaData.ao_statement && (
            <Box mt={3}>
              <Typography variant="h6">Advisory Opinion</Typography>
              <Typography paragraph>{pmaData.ao_statement}</Typography>
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

export default PmaModal;
